// 2026-05-05 v6 — Constantes compartidas del Domo Cero.
//
// El Tripulante camina sobre un círculo limitado (WALK_BOUNDARY_RADIUS)
// y se topa con un anillo solido (RING_OBSTACLE_*) que sólo puede
// cruzar saltando. Estas constantes viven acá para que LocalAvatar,
// MovementPlane y el rendering del anillo lean los mismos números —
// sin esto el clamp del clic y el clamp del avance se pueden
// desfasar y producir glitches del tipo "el avatar dispara contra
// la pared".

import type { Vector3Tuple } from "@domo/shared"

/** Radio máximo en X/Z donde el avatar puede caminar. Levemente
 *  menor que el radio del piso del Domo (28 m en la GLB v3) para
 *  que el cuerpo no se entierre en el muro.
 *
 *  2026-05-06 v3 — Subido de 16 a 26 para que el Tripulante pueda
 *  caminar hasta cerca de la pared del biopolímero (Floor_White
 *  llega a radio 28, Dome_Shell también: medido leyendo los
 *  accessor.min/max de la GLB Domo_3.3). El margen de 2 m evita
 *  que el cuerpo se cruce con la malla de la pared. La cámara
 *  ahora se autoclamp en CameraRig: cuando el avatar se acerca al
 *  borde, OrbitControls.maxDistance se reduce dinámicamente para
 *  que la cámara nunca salga del Domo (ese límite duro queda en
 *  CAMERA_SAFE_RADIUS_XZ = 27.5). */
export const WALK_BOUNDARY_RADIUS = 26

/** Anillo obstáculo cyan al ras del piso. Centro en el origen. El
 *  Tripulante NO puede cruzar caminando — se detiene al borde. Si
 *  salta y la altura del cuerpo supera RING_OBSTACLE_HEIGHT
 *  durante el arco del salto, el cruce es válido. Mantenemos esto
 *  acá (no en la GLB) porque el GLB sólo describe la malla visual;
 *  la lógica de colisión vive en código del cliente. */
export const RING_OBSTACLE_CENTER: Vector3Tuple = [0, 0, 0]
export const RING_OBSTACLE_RADIUS = 5
export const RING_OBSTACLE_HEIGHT = 0.5
/** Banda visible del aro: grosor radial. Solo cosmético — la
 *  colisión usa RING_OBSTACLE_RADIUS exacto. */
export const RING_OBSTACLE_BAND = 0.16

/** Pulgada hacia adentro al frenar contra el aro: la posición
 *  clamped queda EPS metros antes del borde para evitar que el
 *  avatar se quede pegado al material y dispare jitter. */
export const RING_OBSTACLE_EPS = 0.04

/** 2026-05-05 v7 — Apertura del aro. La pared holográfica tiene una
 *  brecha física por la que el Tripulante puede pasar caminando
 *  sin saltar. Tamaño total de la apertura: ~1/8 del círculo (45°).
 *  El centro angular determina dónde está la "puerta": +X (ángulo 0)
 *  por convención (lado este del Domo). Si más adelante queremos
 *  rotar la puerta, basta con cambiar `RING_GAP_CENTER`. */
export const RING_GAP_CENTER = 0
export const RING_GAP_HALF = Math.PI / 8 // 22.5° a cada lado → 45° total

/** 2026-05-06 — Portal de Reconfiguración (anillo dorado).
 *  Ubicado al lado +X del Domo, fuera del aro central (que tiene
 *  radio 5). El Tripulante cruza el aro por el gap y llega al
 *  portal dorado; cuando entra al círculo de proximidad, se le
 *  ofrece reconfigurar su firma (volver al selector). */
export const ELECTION_PORTAL_CENTER: Vector3Tuple = [9, 0, 0]
/** Radio del anillo dorado visible. */
export const ELECTION_PORTAL_RADIUS = 1.6
/** Radio del círculo de proximidad: si el avatar entra, se ofrece
 *  reconfigurar la firma.
 *  2026-05-06 — Bajado de 1.7 a 1.3. Antes el prompt aparecía
 *  cuando el avatar estaba CERCA del aro (incluso fuera de él),
 *  rompiendo la idea de "umbral" que el aro dorado proyecta.
 *  Ahora el prompt sólo aparece DENTRO del círculo (radio 1.3 <
 *  ELECTION_PORTAL_RADIUS = 1.6, así queda con un colchón hacia
 *  el muro dorado). El Tripulante tiene que cruzar el aro por
 *  el gap y entrar al área antes de que se ofrezca cambiar la firma. */
export const ELECTION_PORTAL_PROX_RADIUS = 1.3
/** Apertura del anillo dorado, simétrica como el aro central. La
 *  brecha mira hacia el centro del Domo (-X) para que el Tripulante
 *  entre al portal naturalmente desde el campo. */
export const ELECTION_PORTAL_GAP_CENTER = Math.PI // -X
export const ELECTION_PORTAL_GAP_HALF = Math.PI / 7

/** Devuelve true si el punto (x, z) está dentro del círculo de
 *  proximidad del portal de reconfiguración. */
export function isOnElectionPortal(x: number, z: number): boolean {
    const dx = x - ELECTION_PORTAL_CENTER[0]
    const dz = z - ELECTION_PORTAL_CENTER[2]
    return dx * dx + dz * dz < ELECTION_PORTAL_PROX_RADIUS * ELECTION_PORTAL_PROX_RADIUS
}

// ── Helpers de geometría ─────────────────────────────────────

/** Distancia 2D al origen (ignora Y). */
export function distXZ(x: number, z: number): number {
    return Math.sqrt(x * x + z * z)
}

/** Clamp circular del punto al boundary del Domo. Devuelve el punto
 *  igual si ya está dentro; si está afuera lo proyecta al borde. */
export function clampToBoundary(
    x: number,
    z: number,
    radius = WALK_BOUNDARY_RADIUS
): { x: number; z: number } {
    const r = distXZ(x, z)
    if (r <= radius) return { x, z }
    const k = radius / Math.max(0.0001, r)
    return { x: x * k, z: z * k }
}

/** Normaliza un ángulo a [-π, π]. */
function wrapAngle(a: number): number {
    let x = a
    while (x > Math.PI) x -= Math.PI * 2
    while (x < -Math.PI) x += Math.PI * 2
    return x
}

/** Calcula el primer punto donde el segmento P→T toca el círculo
 *  del aro (sin pull-back), o `null` si el segmento no cruza el
 *  círculo desde un lado al otro. Usado tanto por la detección de
 *  cruce (¿está dentro de la apertura?) como por el clamp al borde. */
function ringIntersection(
    px: number,
    pz: number,
    tx: number,
    tz: number
): { x: number; z: number; t: number } | null {
    const dx = tx - px
    const dz = tz - pz
    const a = dx * dx + dz * dz
    if (a < 1e-6) return null
    const b = 2 * (px * dx + pz * dz)
    const c = px * px + pz * pz - RING_OBSTACLE_RADIUS * RING_OBSTACLE_RADIUS
    const disc = b * b - 4 * a * c
    if (disc < 0) return null
    const sd = Math.sqrt(disc)
    const t1 = (-b - sd) / (2 * a)
    const t2 = (-b + sd) / (2 * a)
    const candidates = [t1, t2].filter((t) => t > 0 && t <= 1)
    if (candidates.length === 0) return null
    const t = Math.min(...candidates)
    return { x: px + dx * t, z: pz + dz * t, t }
}

/** True si el ángulo del punto cae dentro de la apertura del aro. */
function angleInGap(angle: number): boolean {
    const diff = wrapAngle(angle - RING_GAP_CENTER)
    return Math.abs(diff) <= RING_GAP_HALF
}

/** Devuelve true si el segmento P→T cruza la pared sólida del aro
 *  (es decir, sale/entra al círculo POR FUERA de la apertura). Si
 *  el segmento atraviesa el círculo justo por el ángulo de la
 *  brecha, el paso es libre y devolvemos false. */
export function pathCrossesRing(
    px: number,
    pz: number,
    tx: number,
    tz: number
): boolean {
    const rP = distXZ(px, pz)
    const rT = distXZ(tx, tz)
    const inP = rP < RING_OBSTACLE_RADIUS
    const inT = rT < RING_OBSTACLE_RADIUS
    if (inP === inT) return false
    const hit = ringIntersection(px, pz, tx, tz)
    if (!hit) return false
    const ang = Math.atan2(hit.z, hit.x)
    return !angleInGap(ang)
}

// ── Colisión con el portal de Reconfiguración (anillo dorado) ──────
//
// 2026-05-06 — El anillo dorado del portal de Reconfiguración pasa
// a ser sólido como el aro central: el Tripulante sólo puede entrar
// por el gap. Hasta acá el portal era visual: el avatar podía
// atravesar la pared dorada y la sensación de "este lugar es un
// umbral" se diluía. Ahora el segmento P→T se intersecta contra
// el círculo de radio ELECTION_PORTAL_RADIUS centrado en
// ELECTION_PORTAL_CENTER, y si el cruce cae fuera del gap angular
// (ELECTION_PORTAL_GAP_CENTER ± ELECTION_PORTAL_GAP_HALF), el
// avatar se topa contra la pared.

/** Intersección genérica P→T contra un círculo de centro (cx,cz)
 *  y radio r. Devuelve el primer punto donde el segmento cruza el
 *  círculo (t más chico positivo en (0,1]) o `null` si no cruza. */
function ringIntersectionAt(
    px: number,
    pz: number,
    tx: number,
    tz: number,
    cx: number,
    cz: number,
    radius: number
): { x: number; z: number; t: number } | null {
    const dpx = px - cx
    const dpz = pz - cz
    const dx = tx - px
    const dz = tz - pz
    const a = dx * dx + dz * dz
    if (a < 1e-6) return null
    const b = 2 * (dpx * dx + dpz * dz)
    const c = dpx * dpx + dpz * dpz - radius * radius
    const disc = b * b - 4 * a * c
    if (disc < 0) return null
    const sd = Math.sqrt(disc)
    const t1 = (-b - sd) / (2 * a)
    const t2 = (-b + sd) / (2 * a)
    const candidates = [t1, t2].filter((t) => t > 0 && t <= 1)
    if (candidates.length === 0) return null
    const t = Math.min(...candidates)
    return { x: px + dx * t, z: pz + dz * t, t }
}

/** True si el segmento P→T cruza la pared sólida del portal de
 *  reconfiguración (cruzando el círculo POR FUERA de la apertura). */
export function pathCrossesElectionPortal(
    px: number,
    pz: number,
    tx: number,
    tz: number
): boolean {
    const cx = ELECTION_PORTAL_CENTER[0]
    const cz = ELECTION_PORTAL_CENTER[2]
    const dpx = px - cx
    const dpz = pz - cz
    const dtx = tx - cx
    const dtz = tz - cz
    const rP = Math.sqrt(dpx * dpx + dpz * dpz)
    const rT = Math.sqrt(dtx * dtx + dtz * dtz)
    const inP = rP < ELECTION_PORTAL_RADIUS
    const inT = rT < ELECTION_PORTAL_RADIUS
    if (inP === inT) return false
    const hit = ringIntersectionAt(
        px,
        pz,
        tx,
        tz,
        cx,
        cz,
        ELECTION_PORTAL_RADIUS
    )
    if (!hit) return false
    // Ángulo del punto de impacto relativo al CENTRO DEL PORTAL.
    const ang = Math.atan2(hit.z - cz, hit.x - cx)
    const diff = wrapAngle(ang - ELECTION_PORTAL_GAP_CENTER)
    return Math.abs(diff) > ELECTION_PORTAL_GAP_HALF
}

/** Punto donde el segmento P→T toca el círculo del portal,
 *  con epsilon hacia P para no dejar al avatar pegado a la pared. */
export function projectToElectionPortalEdge(
    px: number,
    pz: number,
    tx: number,
    tz: number
): { x: number; z: number } {
    const cx = ELECTION_PORTAL_CENTER[0]
    const cz = ELECTION_PORTAL_CENTER[2]
    const hit = ringIntersectionAt(
        px,
        pz,
        tx,
        tz,
        cx,
        cz,
        ELECTION_PORTAL_RADIUS
    )
    if (!hit) return { x: tx, z: tz }
    const dx = tx - px
    const dz = tz - pz
    const a = dx * dx + dz * dz
    if (a < 1e-6) return { x: px, z: pz }
    const pullback = RING_OBSTACLE_EPS / Math.sqrt(a)
    const t = Math.max(0, hit.t - pullback)
    return { x: px + dx * t, z: pz + dz * t }
}

/** Punto donde el segmento P→T toca el círculo del aro (radio
 *  RING_OBSTACLE_RADIUS centrado en origen). Aplica un epsilon
 *  hacia P para que el avatar se detenga ANTES de tocar la pared.
 *  Si por alguna razón la línea no intersecta (caso degenerado),
 *  devolvemos T sin clamp. */
export function projectToRingEdge(
    px: number,
    pz: number,
    tx: number,
    tz: number
): { x: number; z: number } {
    const dx = tx - px
    const dz = tz - pz
    const a = dx * dx + dz * dz
    if (a < 1e-6) return { x: px, z: pz }
    const b = 2 * (px * dx + pz * dz)
    const c = px * px + pz * pz - RING_OBSTACLE_RADIUS * RING_OBSTACLE_RADIUS
    const disc = b * b - 4 * a * c
    if (disc < 0) return { x: tx, z: tz }
    const sd = Math.sqrt(disc)
    const t1 = (-b - sd) / (2 * a)
    const t2 = (-b + sd) / (2 * a)
    // Buscamos el primer corte yendo desde P (t pequeño positivo).
    const candidates = [t1, t2].filter((t) => t > 0 && t <= 1)
    if (candidates.length === 0) return { x: tx, z: tz }
    let t = Math.min(...candidates)
    // Pull-back: clamp un poquito antes del borde.
    const pullback = RING_OBSTACLE_EPS / Math.sqrt(a)
    t = Math.max(0, t - pullback)
    return { x: px + dx * t, z: pz + dz * t }
}
